import React, { useState, useEffect } from 'react';
import { canManageFinance } from '@/lib/permissions';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, UserCog, Mail, Phone, Upload, X, User, CreditCard, Briefcase, Search, ArrowUpDown, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const Managers = ({ user }) => {
  const [managers, setManagers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  // Filter state - combined dropdown
  const [filterCombined, setFilterCombined] = useState('all');
  // Image preview dialog state
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [imagePreviewTitle, setImagePreviewTitle] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    no_ktp: '',
    nama_ktp: '',
    status: 'ACTIVE',
    position: 'Mitra',
    keterangan: '',
    subordinates: [],
    photo_url: '',
    id_card_url: '',
    branch_shares: [],
    accounts: [],
    order_number: 9999
  });
  const [subordinateForm, setSubordinateForm] = useState({
    manager_id: ''
  });
  const [shareForm, setShareForm] = useState({
    branch_id: '',
    percentage: ''
  });
  const [idCardFile, setIdCardFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [idCardPreview, setIdCardPreview] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadingIdCard, setUploadingIdCard] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [idCardDragging, setIdCardDragging] = useState(false);
  const [photoDragging, setPhotoDragging] = useState(false);

  // DnD Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [managersRes, branchesRes] = await Promise.all([
        api.get('/managers'),
        api.get('/branches')
      ]);
      setManagers(managersRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event, status) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Get managers for this status
    const statusManagers = managers.filter(m => m.status === status);
    const oldIndex = statusManagers.findIndex(m => m.id === active.id);
    const newIndex = statusManagers.findIndex(m => m.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder locally
    const reordered = arrayMove(statusManagers, oldIndex, newIndex);
    
    // Update order numbers
    const updates = reordered.map((m, idx) => ({
      id: m.id,
      order_number: idx + 1
    }));

    // Update UI immediately
    const updatedManagers = managers.map(m => {
      const update = updates.find(u => u.id === m.id);
      return update ? { ...m, order_number: update.order_number } : m;
    });
    setManagers(updatedManagers);

    // Send to backend
    try {
      await api.put('/managers/reorder', { items: updates, status });
      toast.success('Urutan berhasil diperbarui');
      // Refresh data to ensure UI shows updated order
      await fetchData();
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Gagal memperbarui urutan: ' + (error.response?.data?.detail || error.message));
      // Revert on error
      fetchData();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // No. KTP hanya boleh angka (boleh diawali angka 0)
    const noKtp = (formData.no_ktp || '').trim();
    if (noKtp && !/^[0-9]+$/.test(noKtp)) {
      toast.error('No. KTP hanya boleh berisi angka');
      return;
    }

    setLoading(true);

    try {
      // Convert empty strings to null for optional fields
      const submitData = {
        ...formData,
        phone: formData.phone.trim() || null,
        no_ktp: formData.no_ktp.trim() || null,
        nama_ktp: formData.nama_ktp.trim() || null,
        keterangan: (formData.keterangan || '').trim() || null,
      };

      let managerId;
      
      if (editingManager) {
        await api.put(`/managers/${editingManager.id}`, submitData);
        managerId = editingManager.id;
        toast.success('Mitra berhasil diperbarui');
      } else {
        const response = await api.post('/managers', submitData);
        managerId = response.data.id;
        
        // Upload images if new manager and files are selected
        if (idCardFile) {
          try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', idCardFile);
            await api.post(`/upload-image?image_type=id_card&manager_id=${managerId}`, uploadFormData);
          } catch (error) {
            console.error('Failed to upload ID card:', error);
            toast.error('Mitra dibuat tapi gagal upload KTP');
          }
        }
        
        if (photoFile) {
          try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', photoFile);
            await api.post(`/upload-image?image_type=photo&manager_id=${managerId}`, uploadFormData);
          } catch (error) {
            console.error('Failed to upload photo:', error);
            toast.error('Mitra dibuat tapi gagal upload foto');
          }
        }
        
        toast.success('Mitra berhasil ditambahkan');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      // Handle validation errors from backend
      let errorMessage = 'Operasi gagal';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err.message).join(', ');
        } 
        // If detail is a single validation error object
        else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddShare = () => {
    if (!shareForm.branch_id || !shareForm.percentage) {
      toast.error('Cabang dan persentase harus diisi');
      return;
    }

    const branch = branches.find(b => b.id === shareForm.branch_id);
    if (!branch) return;

    // Check if branch already added
    if (formData.branch_shares.some(s => s.branch_id === shareForm.branch_id)) {
      toast.error('Cabang sudah ditambahkan');
      return;
    }

    // VALIDATION FOR MANAGER: Manager cannot have shares in branch that already has another manager
    if (formData.position === 'Manager') {
      // Check if the branch already has a manager (different from current editing manager)
      const branchManagerId = branch.manager_id;
      const currentManagerId = editingManager?.id; // ID of manager being edited
      
      // Find the manager of this branch
      const branchManager = managers.find(m => m.id === branchManagerId);
      
      // If branch has a manager AND it's not the current manager being edited AND that manager is position='Manager'
      if (branchManager && branchManager.position === 'Manager' && branchManagerId !== currentManagerId) {
        toast.error(`Manager tidak bisa memiliki saham di cabang yang sudah memiliki manager lain. Cabang ${branch.name} sudah dikelola oleh ${branchManager.name}.`);
        return;
      }
    }

    const newShare = {
      branch_id: shareForm.branch_id,
      branch_name: branch.name,
      percentage: parseFloat(shareForm.percentage)
    };

    setFormData({
      ...formData,
      branch_shares: [...formData.branch_shares, newShare]
    });

    setShareForm({ branch_id: '', percentage: '' });
    toast.success('Saham cabang ditambahkan');
  };

  const handleRemoveShare = (index) => {
    const updatedShares = formData.branch_shares.filter((_, i) => i !== index);
    setFormData({ ...formData, branch_shares: updatedShares });
    toast.success('Saham cabang dihapus');
  };

  const handleAddSubordinate = () => {
    if (!subordinateForm.manager_id) {
      toast.error('Pilih mitra terlebih dahulu');
      return;
    }

    if (formData.subordinates.includes(subordinateForm.manager_id)) {
      toast.error('Mitra sudah ditambahkan');
      return;
    }

    const selectedManager = managers.find(m => m.id === subordinateForm.manager_id);
    if (!selectedManager) {
      toast.error('Mitra tidak ditemukan');
      return;
    }

    setFormData({
      ...formData,
      subordinates: [...formData.subordinates, subordinateForm.manager_id]
    });
    setSubordinateForm({ manager_id: '' });
    toast.success('Mitra berhasil ditambahkan ke naungan');
  };

  const handleRemoveSubordinate = (managerId) => {
    const updatedSubordinates = formData.subordinates.filter(id => id !== managerId);
    setFormData({ ...formData, subordinates: updatedSubordinates });
    toast.success('Mitra dihapus dari naungan');
  };

  const handleDeleteImage = async (imageType) => {
    if (!editingManager || !editingManager.id) {
      // Just clear preview for new manager
      if (imageType === 'id_card') {
        setIdCardFile(null);
        setIdCardPreview('');
        setFormData(prev => ({...prev, id_card_url: ''}));
      } else {
        setPhotoFile(null);
        setPhotoPreview('');
        setFormData(prev => ({...prev, photo_url: ''}));
      }
      return;
    }

    // Confirm deletion for existing manager
    if (!window.confirm(`Apakah Anda yakin ingin menghapus ${imageType === 'id_card' ? 'KTP' : 'foto profil'} ini?`)) {
      return;
    }

    try {
      const endpoint = imageType === 'id_card' ? 'id_cards' : 'photos';
      await api.delete(`/managers/${editingManager.id}/image/${endpoint}`);
      
      // Clear preview and state
      if (imageType === 'id_card') {
        setIdCardFile(null);
        setIdCardPreview('');
        setFormData(prev => ({...prev, id_card_url: ''}));
      } else {
        setPhotoFile(null);
        setPhotoPreview('');
        setFormData(prev => ({...prev, photo_url: ''}));
      }
      
      // Update managers state immediately to reflect deletion in table view
      setManagers(prevManagers => 
        prevManagers.map(m => 
          m.id === editingManager.id 
            ? { 
                ...m, 
                has_id_card: imageType === 'id_card' ? false : m.has_id_card,
                has_photo: imageType === 'photo' ? false : m.has_photo
              }
            : m
        )
      );
      
      toast.success(`${imageType === 'id_card' ? 'KTP' : 'Foto profil'} berhasil dihapus`);
      
      // Refresh data to ensure consistency
      await fetchData();
    } catch (error) {
      console.error('Delete image error:', error);
      toast.error('Gagal menghapus gambar');
    }
  };

  const handleIDCardUpload = async (file) => {
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }
    
    setIdCardFile(file);
    setUploadingIdCard(true);
    
    try {
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdCardPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Only upload to server if editing existing manager
      if (editingManager && editingManager.id) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        const response = await api.post(`/upload-image?image_type=id_card&manager_id=${editingManager.id}`, uploadFormData);
        
        if (response.data.success) {
          setFormData(prev => ({ ...prev, id_card_url: response.data.url }));
          const ratio = response.data.compression_ratio;
          toast.success(`KTP berhasil diupload & dikompres ${ratio}`);
        }
      } else {
        // For new manager, just preview - will upload after manager is created
        toast.info('KTP akan diupload setelah mitra disimpan');
      }
    } catch (error) {
      console.error('Failed to upload ID card:', error);
      toast.error('Gagal upload KTP');
    } finally {
      setUploadingIdCard(false);
    }
  };

  const handleIDCardChange = (e) => {
    const file = e.target.files[0];
    if (file) handleIDCardUpload(file);
  };

  const handleIDCardDrop = (e) => {
    e.preventDefault();
    setIdCardDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleIDCardUpload(file);
  };

  const handleIDCardDragOver = (e) => {
    e.preventDefault();
    setIdCardDragging(true);
  };

  const handleIDCardDragLeave = () => {
    setIdCardDragging(false);
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }
    
    setPhotoFile(file);
    setUploadingPhoto(true);
    
    try {
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Only upload to server if editing existing manager
      if (editingManager && editingManager.id) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        const response = await api.post(`/upload-image?image_type=photo&manager_id=${editingManager.id}`, uploadFormData);
        
        if (response.data.success) {
          setFormData(prev => ({ ...prev, photo_url: response.data.url }));
          const ratio = response.data.compression_ratio;
          toast.success(`Foto berhasil diupload & dikompres ${ratio}`);
        }
      } else {
        // For new manager, just preview - will upload after manager is created
        toast.info('Foto akan diupload setelah mitra disimpan');
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Gagal upload foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) handlePhotoUpload(file);
  };

  const handlePhotoDrop = (e) => {
    e.preventDefault();
    setPhotoDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handlePhotoUpload(file);
  };

  const handlePhotoDragOver = (e) => {
    e.preventDefault();
    setPhotoDragging(true);
  };

  const handlePhotoDragLeave = () => {
    setPhotoDragging(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus mitra ini?')) return;

    try {
      await api.delete(`/managers/${id}`);
      toast.success('Mitra berhasil dihapus');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus mitra');
    }
  };

  const openEditDialog = (manager) => {
    setEditingManager(manager);
    
    // Ensure all accounts have balance field (for backward compatibility)
    const normalizedAccounts = (manager.accounts || []).map(acc => ({
      ...acc,
      balance: acc.balance !== undefined ? acc.balance : (acc.initial_balance || 0)
    }));
    
    setFormData({
      name: manager.name,
      phone: manager.phone || '',
      no_ktp: manager.no_ktp || '',
      nama_ktp: manager.nama_ktp || '',
      status: manager.status || 'ACTIVE',
      position: manager.position || 'Mitra',
      keterangan: manager.keterangan || '',
      subordinates: manager.subordinates || [],
      photo_url: manager.photo_url || '',
      id_card_url: manager.id_card_url || '',
      branch_shares: manager.branch_shares || [],
      accounts: normalizedAccounts,
      order_number: manager.order_number || 9999
    });
    
    // Auto-fill percentage for Mitra when editing (use first branch share percentage)
    if (manager.position !== 'Manager' && manager.branch_shares && manager.branch_shares.length > 0) {
      setShareForm({ 
        branch_id: '', 
        percentage: manager.branch_shares[0].percentage.toString() 
      });
    } else if (manager.position === 'Manager' && manager.branch_shares && manager.branch_shares.length > 0) {
      // For Manager, also set percentage
      setShareForm({ 
        branch_id: '', 
        percentage: manager.branch_shares[0].percentage.toString() 
      });
    }
    
    // Set preview URLs for existing images from S3 URLs in database
    if (manager.id_card_url) {
      setIdCardPreview(manager.id_card_url);
    } else {
      setIdCardPreview('');
    }
    
    if (manager.photo_url) {
      setPhotoPreview(manager.photo_url);
    } else {
      setPhotoPreview('');
    }
    
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      no_ktp: '',
      nama_ktp: '',
      status: 'ACTIVE',
      position: 'Mitra',
      keterangan: '',
      subordinates: [],
      photo_url: '',
      id_card_url: '',
      branch_shares: [],
      accounts: [],
      order_number: 9999
    });
    setShareForm({ branch_id: '', percentage: '' });
    setSubordinateForm({ manager_id: '' });
    setEditingManager(null);
    setIdCardFile(null);
    setPhotoFile(null);
    setIdCardPreview('');
    setPhotoPreview('');
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Unknown Branch';
  };

  // Helper function to sort branches by status: ACTIVE → INACTIVE → BLACKLIST
  const getSortedBranches = (branchesList) => {
    const statusOrder = { 'ACTIVE': 1, 'INACTIVE': 2, 'BLACKLIST': 3 };
    return [...branchesList].sort((a, b) => {
      const aStatus = a.status || 'ACTIVE';
      const bStatus = b.status || 'ACTIVE';
      const aOrder = statusOrder[aStatus] || 4;
      const bOrder = statusOrder[bStatus] || 4;
      
      // Primary sort: by status
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      
      // Secondary sort: by order_number within same status group
      const aOrderNum = a.order_number || 9999;
      const bOrderNum = b.order_number || 9999;
      
      if (aOrderNum !== bOrderNum) {
        return aOrderNum - bOrderNum;
      }
      
      // Tertiary sort: by name alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  const canManageManagers = canManageFinance(user);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Open image preview dialog
  const openImagePreview = (url, title) => {
    setImagePreviewUrl(url);
    setImagePreviewTitle(title);
    setImagePreviewOpen(true);
  };

  // Filter managers based on search query and combined filter
  const filteredManagers = managers.filter(manager => {
    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        manager.name.toLowerCase().includes(query) ||
        (manager.phone && manager.phone.toLowerCase().includes(query)) ||
        (manager.nama_ktp && manager.nama_ktp.toLowerCase().includes(query)) ||
        (manager.no_ktp && manager.no_ktp.toLowerCase().includes(query)) ||
        manager.position.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }

    // Combined filter
    if (filterCombined === 'all') return true;
    
    // Use has_photo and has_id_card flags from backend
    const hasIdCard = Boolean(manager.has_id_card);
    const hasPhoto = Boolean(manager.has_photo);
    const status = manager.status || 'ACTIVE';
    
    switch(filterCombined) {
      case 'active': return status === 'ACTIVE';
      case 'inactive': return status === 'INACTIVE';
      case 'blacklist': return status === 'BLACKLIST';
      case 'ktp-uploaded': return hasIdCard;
      case 'ktp-not-uploaded': return !hasIdCard;
      case 'photo-uploaded': return hasPhoto;
      case 'photo-not-uploaded': return !hasPhoto;
      default: return true;
    }
  });

  // Sort filtered managers
  const sortedManagers = [...filteredManagers].sort((a, b) => {
    // Priority 1: Sort by order_number within each status group
    // This ensures drag-and-drop order is respected
    const aOrder = a.order_number || 9999;
    const bOrder = b.order_number || 9999;
    
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    
    // Priority 2: If order_number is the same (or both missing), use sortField
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    // Convert to lowercase for string comparison
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Sortable Row Component for drag-drop
  const SortableManagerRow = ({ manager, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: manager.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    // Helper function to get branch name by ID
    const getBranchName = (branchId) => {
      const branch = branches.find(b => b.id === branchId);
      return branch ? branch.name : 'Cabang Tidak Ditemukan';
    };

    return (
      <TableRow ref={setNodeRef} style={style} key={manager.id} data-testid={`manager-row-${manager.id}`}>
        <TableCell>
          <div className="flex items-center gap-2">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
              <GripVertical size={16} className="text-slate-400" />
            </div>
            <span className="font-medium">{index + 1}</span>
          </div>
        </TableCell>
        <TableCell className="font-medium">
          <div className="flex items-center gap-3">
            {manager.photo_url ? (
              <img 
                src={manager.photo_url}
                alt={manager.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                className="w-10 h-10 rounded-full object-cover border-2 border-teal-200 cursor-pointer hover:scale-110 transition-transform"
                onClick={() => openImagePreview(manager.photo_url, `Foto Profil - ${manager.name}`)}
              />
            ) : null}
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center" style={{display: manager.photo_url ? 'none' : 'flex'}}>
              <UserCog size={18} className="text-purple-600" />
            </div>
            <span>{manager.name}</span>
          </div>
        </TableCell>
        <TableCell>
          <Badge 
            variant="outline" 
            className={
              manager.position === 'Manager' 
                ? "bg-amber-50 text-amber-700 border-amber-300 font-medium" 
                : "bg-slate-100 text-slate-700 border-slate-300"
            }
          >
            {manager.position || 'Mitra'}
          </Badge>
        </TableCell>
        <TableCell>
          <span className="text-sm text-slate-700" data-testid={`manager-nama-ktp-${manager.id}`}>
            {manager.nama_ktp || '-'}
          </span>
        </TableCell>
        <TableCell>
          <span className="text-sm text-slate-700" data-testid={`manager-no-ktp-${manager.id}`}>
            {manager.no_ktp || '-'}
          </span>
        </TableCell>
        <TableCell>
          {manager.id_card_url ? (
            <div className="flex items-center gap-1">
              <img 
                src={manager.id_card_url}
                alt="KTP" 
                className="w-8 h-8 object-cover rounded border cursor-pointer hover:scale-110 transition-transform"
                onClick={() => openImagePreview(manager.id_card_url, `KTP - ${manager.name}`)}
                onError={(e) => {
                  e.target.parentElement.innerHTML = '<span class="text-slate-400 text-sm">-</span>';
                }}
              />
              <span className="text-green-600 font-bold text-sm">✓</span>
            </div>
          ) : (
            <span className="text-slate-400 text-sm">-</span>
          )}
        </TableCell>
        <TableCell>
          {manager.branch_shares && manager.branch_shares.length > 0 ? (
            manager.position === 'Manager' ? (
              // Manager: Show percentage and branch count/list
              <div className="space-y-1">
                <div className="text-sm">
                  <span className="text-teal-600 font-semibold text-base">
                    {parseFloat(manager.branch_shares[0].percentage.toFixed(2))}%
                  </span>
                  <span className="text-slate-500 text-xs ml-1">
                    ({manager.branch_shares.length} cabang)
                  </span>
                </div>
                {manager.branch_shares.length <= 3 ? (
                  // Show branch names if 3 or less
                  <div className="flex flex-wrap gap-1">
                    {manager.branch_shares.map((share, idx) => (
                      <span key={idx} className="text-xs text-purple-600 font-medium">
                        {getBranchName(share.branch_id)}{idx < manager.branch_shares.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                ) : (
                  // Show first 3 + "... dan X lainnya"
                  <div className="flex flex-wrap gap-1">
                    {manager.branch_shares.slice(0, 3).map((share, idx) => (
                      <span key={idx} className="text-xs text-purple-600 font-medium">
                        {getBranchName(share.branch_id)},
                      </span>
                    ))}
                    <span className="text-xs text-slate-500 italic">
                      ... dan {manager.branch_shares.length - 3} lainnya
                    </span>
                  </div>
                )}
              </div>
            ) : (
              // Mitra: Show detailed per branch
              <div className="space-y-1">
                {manager.branch_shares.map((share, idx) => {
                  const formattedPercentage = parseFloat(share.percentage.toFixed(2));
                  return (
                    <div key={idx} className="text-sm">
                      <span className="text-slate-700">{getBranchName(share.branch_id)}:</span>
                      <span className="text-teal-600 font-semibold ml-1">{formattedPercentage}%</span>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <span className="text-slate-400 italic text-sm">-</span>
          )}
        </TableCell>
        <TableCell className="max-w-[220px]">
          <span
            className="text-sm text-slate-700 whitespace-pre-wrap break-words"
            title={manager.keterangan || ''}
            data-testid={`manager-keterangan-${manager.id}`}
          >
            {manager.keterangan || '-'}
          </span>
        </TableCell>
        <TableCell>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => openEditDialog(manager)}>
              <Edit size={14} />
            </Button>
            {canManageManagers && (
              <Button variant="destructive" size="sm" onClick={() => handleDelete(manager.id)}>
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  if (loading && managers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="managers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>Manajemen Mitra</h1>
          <p className="text-slate-600 mt-1">Kelola mitra dan kepemilikan saham mereka di cabang</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Search Input */}
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              type="text"
              placeholder="Cari nama, nama KTP, no. KTP, telepon, atau jabatan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {canManageManagers && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-manager-btn">
                <Plus size={20} className="mr-2" />
                Tambah Mitra
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">{editingManager ? 'Edit Mitra' : 'Tambah Mitra Baru'}</DialogTitle>
                <p className="text-sm text-slate-500">Isi informasi mitra dengan lengkap</p>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informasi Dasar */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-teal-600 font-semibold">
                    <User size={18} />
                    <span>Informasi Dasar</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium">Nama Mitra *</Label>
                      <Input
                        id="name"
                        data-testid="manager-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium">Nomor Telepon</Label>
                      <Input
                        id="phone"
                        data-testid="manager-phone-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Jabatan *</Label>
                      <SearchableSelect
                        value={formData.position}
                        onValueChange={(value) => setFormData({ ...formData, position: value })}
                        options={[
                          { value: 'Mitra', label: 'Mitra' },
                          { value: 'Manager', label: 'Manager' }
                        ]}
                        placeholder="Pilih jabatan..."
                        searchPlaceholder="Cari jabatan..."
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Status *</Label>
                      <SearchableSelect
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                        options={[
                          { value: 'ACTIVE', label: 'Aktif' },
                          { value: 'INACTIVE', label: 'Non-Aktif' },
                          { value: 'BLACKLIST', label: 'Blacklist' }
                        ]}
                        placeholder="Pilih status..."
                        searchPlaceholder="Cari status..."
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label className="text-sm font-medium">Keterangan</Label>
                    <Textarea
                      value={formData.keterangan}
                      onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                      placeholder="Catatan tambahan tentang mitra (opsional)"
                      className="mt-1"
                      rows={2}
                      data-testid="manager-keterangan-input"
                    />
                  </div>
                </div>

                {/* Upload Dokumen */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-teal-600 font-semibold">
                    <CreditCard size={18} />
                    <span>Dokumen Identitas (Opsional)</span>
                  </div>
                  
                  {/* No KTP Input */}
                  <div>
                    <Label htmlFor="no_ktp" className="text-sm font-medium">No KTP</Label>
                    <Input
                      id="no_ktp"
                      data-testid="manager-ktp-input"
                      value={formData.no_ktp}
                      onChange={(e) => setFormData({ ...formData, no_ktp: e.target.value })}
                      placeholder="Masukkan nomor KTP (angka saja)"
                      maxLength={16}
                      inputMode="numeric"
                      className="mt-1"
                      data-testid="manager-no-ktp-input"
                    />
                  </div>
                  
                  {/* Nama KTP Input */}
                  <div>
                    <Label htmlFor="nama_ktp" className="text-sm font-medium">Nama KTP</Label>
                    <Input
                      id="nama_ktp"
                      data-testid="manager-nama-ktp-input"
                      value={formData.nama_ktp}
                      onChange={(e) => setFormData({ ...formData, nama_ktp: e.target.value })}
                      placeholder="Nama sesuai KTP"
                      className="mt-1"
                    />
                  </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* ID Card Upload with Drag & Drop */}
                      <div>
                        <Label className="text-sm font-medium">Kartu Identitas (KTP/SIM)</Label>
                        <div className="mt-2">
                          {idCardPreview ? (
                            <div className="relative">
                              <img src={idCardPreview} alt="ID Card" className="w-full h-56 object-cover rounded border" />
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                className="absolute top-2 right-2"
                                onClick={() => handleDeleteImage('id_card')}
                              >
                                <X size={16} />
                              </Button>
                              {uploadingIdCard && (
                                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded">
                                  <span className="text-white text-sm">Uploading...</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              onDrop={handleIDCardDrop}
                              onDragOver={handleIDCardDragOver}
                              onDragLeave={handleIDCardDragLeave}
                              className={`flex flex-col items-center justify-center w-full h-56 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                idCardDragging 
                                  ? 'bg-teal-50 border-teal-400' 
                                  : 'bg-white hover:bg-slate-50 border-slate-300'
                              }`}
                            >
                              <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer">
                                <Upload className={`w-8 h-8 mb-2 ${idCardDragging ? 'text-teal-600' : 'text-slate-400'}`} />
                                <span className={`text-sm ${idCardDragging ? 'text-teal-600 font-medium' : 'text-slate-500'}`}>
                                  {idCardDragging ? 'Drop file di sini' : 'Drag & drop atau klik untuk upload'}
                                </span>
                                <span className="text-xs text-slate-400 mt-1">KTP/SIM • Max 5MB</span>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/*"
                                  onChange={handleIDCardChange}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Photo Upload with Drag & Drop */}
                      <div>
                        <Label className="text-sm font-medium">Foto Profil</Label>
                        <div className="mt-2">
                          {photoPreview ? (
                            <div className="relative">
                              <img src={photoPreview} alt="Profile" className="w-full h-56 object-cover rounded border" />
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                className="absolute top-2 right-2"
                                onClick={() => handleDeleteImage('photo')}
                              >
                                <X size={16} />
                              </Button>
                              {uploadingPhoto && (
                                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded">
                                  <span className="text-white text-sm">Uploading...</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              onDrop={handlePhotoDrop}
                              onDragOver={handlePhotoDragOver}
                              onDragLeave={handlePhotoDragLeave}
                              className={`flex flex-col items-center justify-center w-full h-56 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                photoDragging 
                                  ? 'bg-teal-50 border-teal-400' 
                                  : 'bg-white hover:bg-slate-50 border-slate-300'
                              }`}
                            >
                              <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer">
                                <Upload className={`w-8 h-8 mb-2 ${photoDragging ? 'text-teal-600' : 'text-slate-400'}`} />
                                <span className={`text-sm ${photoDragging ? 'text-teal-600 font-medium' : 'text-slate-500'}`}>
                                  {photoDragging ? 'Drop file di sini' : 'Drag & drop atau klik untuk upload'}
                                </span>
                                <span className="text-xs text-slate-400 mt-1">JPG, PNG • Max 5MB</span>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/*"
                                  onChange={handlePhotoChange}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                </div>

                {/* Kepemilikan Saham */}
                <div className="space-y-4">
                  {/* Manager: Single percentage for selected branches */}
                  {formData.position === 'Manager' && (
                    <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 text-teal-600 font-semibold">
                        <Briefcase size={18} />
                        <span>Kepemilikan Saham</span>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Step 1: Set Percentage */}
                        <div className="p-3 bg-white rounded-lg border-2 border-teal-200">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold text-sm">
                                1
                              </div>
                            </div>
                            <div className="flex-1">
                              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                                Persentase Kepemilikan Saham
                              </Label>
                              <div className="flex items-center gap-2">
                                <Input
                                  data-testid="manager-share-percentage-input"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  value={shareForm.percentage}
                                  onChange={(e) => {
                                    const newPercentage = e.target.value;
                                    setShareForm({ ...shareForm, percentage: newPercentage });
                                    
                                    // Auto-update all existing shares when percentage changes
                                    if (formData.branch_shares.length > 0) {
                                      const updatedShares = formData.branch_shares.map(share => ({
                                        ...share,
                                        percentage: parseFloat(newPercentage) || 0
                                      }));
                                      setFormData({ ...formData, branch_shares: updatedShares });
                                    }
                                  }}
                                  placeholder="Contoh: 25"
                                  className="flex-1"
                                />
                                <span className="text-sm font-semibold text-slate-600">%</span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                Persentase ini akan berlaku untuk semua cabang yang dipilih
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Step 2: Select Branches */}
                        <div className="p-3 bg-white rounded-lg border-2 border-teal-200">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold text-sm">
                                2
                              </div>
                            </div>
                            <div className="flex-1 space-y-2">
                              <Label className="text-xs font-semibold text-slate-700">
                                Pilih Cabang
                              </Label>
                              <div className="flex items-center gap-2">
                                <SearchableSelect
                                  value={shareForm.branch_id}
                                  onValueChange={(value) => setShareForm({ ...shareForm, branch_id: value })}
                                  options={getSortedBranches(branches)
                                    .filter(b => !formData.branch_shares.some(s => s.branch_id === b.id))
                                    .map(branch => ({
                                      value: branch.id,
                                      label: branch.name
                                    }))}
                                  placeholder="Pilih cabang..."
                                  searchPlaceholder="Cari cabang..."
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => {
                                    if (!shareForm.branch_id) {
                                      toast.error('Pilih cabang terlebih dahulu');
                                      return;
                                    }
                                    
                                    const percentage = parseFloat(shareForm.percentage);
                                    if (!percentage || percentage <= 0 || percentage > 100) {
                                      toast.error('Masukkan persentase saham terlebih dahulu');
                                      return;
                                    }

                                    // Add branch with the current percentage
                                    const branch = branches.find(b => b.id === shareForm.branch_id);
                                    const newShare = {
                                      branch_id: shareForm.branch_id,
                                      branch_name: branch?.name || '',
                                      percentage: percentage
                                    };

                                    setFormData({ 
                                      ...formData, 
                                      branch_shares: [...formData.branch_shares, newShare] 
                                    });
                                    
                                    setShareForm({ ...shareForm, branch_id: '' });
                                    toast.success(`${branch?.name} ditambahkan dengan saham ${percentage}%`);
                                  }}
                                  className="bg-teal-600 hover:bg-teal-700"
                                >
                                  <Plus size={16} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Show current shares */}
                        {formData.branch_shares.length > 0 && (
                          <div className="p-3 bg-white rounded-lg border">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-slate-700">
                                Cabang dengan Saham {formData.branch_shares[0]?.percentage}%
                                <span className="text-slate-500 font-normal ml-1">
                                  ({formData.branch_shares.length} cabang)
                                </span>
                              </p>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setFormData({ ...formData, branch_shares: [] });
                                  toast.success('Semua cabang dihapus');
                                }}
                                className="h-6 text-xs text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={12} className="mr-1" />
                                Hapus Semua
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {formData.branch_shares.map((share, index) => (
                                <div key={index} className="flex items-center gap-1 bg-teal-50 px-2 py-1 rounded border border-teal-200 text-xs">
                                  <span className="font-medium text-teal-700">{share.branch_name}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updatedShares = formData.branch_shares.filter((_, i) => i !== index);
                                      setFormData({ ...formData, branch_shares: updatedShares });
                                      toast.success(`${share.branch_name} dihapus`);
                                    }}
                                    className="text-red-600 hover:text-red-700 ml-1"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Mitra: Different percentages per branch */}
                  {formData.position !== 'Manager' && (
                    <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 text-teal-600 font-semibold">
                        <Briefcase size={18} />
                        <span>Kepemilikan Saham</span>
                      </div>
                      
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-7">
                          <Label className="text-xs">Cabang</Label>
                          <SearchableSelect
                            value={shareForm.branch_id}
                            onValueChange={(value) => setShareForm({ ...shareForm, branch_id: value })}
                            options={getSortedBranches(branches).map(branch => ({
                              value: branch.id,
                              label: branch.name
                            }))}
                            placeholder="Pilih cabang..."
                            searchPlaceholder="Cari cabang..."
                          />
                        </div>
                      <div className="col-span-4">
                        <Label className="text-xs">%</Label>
                        <Input
                          data-testid="share-percentage-input"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={shareForm.percentage}
                          onChange={(e) => setShareForm({ ...shareForm, percentage: e.target.value })}
                        />
                      </div>
                      <div className="col-span-1 flex items-end">
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleAddShare}
                          data-testid="add-share-btn"
                          className="w-full bg-teal-600 hover:bg-teal-700"
                        >
                          <Plus size={16} />
                        </Button>
                      </div>
                    </div>

                    {formData.branch_shares.length > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {formData.branch_shares.map((share, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-2 rounded border text-xs">
                            <div>
                              <span className="font-medium">{share.branch_name}</span>
                              <span className="text-teal-600 font-semibold ml-2">{share.percentage}%</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveShare(index)}
                              className="text-red-600 hover:text-red-700 h-6 w-6 p-0"
                              data-testid={`remove-share-${index}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="manager-submit-btn">
                    {loading ? 'Menyimpan...' : editingManager ? 'Update' : 'Simpan'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Filter Section */}
      {managers.length > 0 && (
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium text-slate-700 whitespace-nowrap">Filter Mitra:</Label>
              <Select value={filterCombined} onValueChange={setFilterCombined}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Semua Mitra" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Mitra</SelectItem>
                  <SelectItem value="active">Status: Aktif</SelectItem>
                  <SelectItem value="inactive">Status: Non-Aktif</SelectItem>
                  <SelectItem value="blacklist">Status: Blacklist</SelectItem>
                  <SelectItem value="ktp-uploaded">Sudah Upload KTP</SelectItem>
                  <SelectItem value="ktp-not-uploaded">Belum Upload KTP</SelectItem>
                  <SelectItem value="photo-uploaded">Sudah Upload Foto</SelectItem>
                  <SelectItem value="photo-not-uploaded">Belum Upload Foto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {managers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserCog size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">Belum ada mitra. Tambahkan mitra pertama Anda.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mitra Aktif */}
          {sortedManagers.filter(m => m.status === 'ACTIVE' || !m.status).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Badge variant="default" className="bg-green-600">Aktif</Badge>
                  Mitra Aktif
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDragEnd(event, 'ACTIVE')}
                >
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">No</TableHead>
                          <TableHead>Nama Mitra</TableHead>
                          <TableHead>Jabatan</TableHead>
                          <TableHead>Nama KTP</TableHead>
                          <TableHead>No. KTP</TableHead>
                          <TableHead>Foto KTP</TableHead>
                          <TableHead>Kepemilikan Saham</TableHead>
                          <TableHead>Keterangan</TableHead>
                          {canManageManagers && <TableHead className="text-right">Aksi</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <SortableContext 
                        items={sortedManagers.filter(m => m.status === 'ACTIVE' || !m.status).map(m => m.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <TableBody>
                          {sortedManagers.filter(m => m.status === 'ACTIVE' || !m.status).map((manager, index) => (
                            <SortableManagerRow key={manager.id} manager={manager} index={index} />
                          ))}
                        </TableBody>
                      </SortableContext>
                    </Table>
                  </div>
                </DndContext>
              </CardContent>
            </Card>
          )}

          {/* Mitra Non-Aktif */}
          {sortedManagers.filter(m => m.status === 'INACTIVE').length > 0 && (
            <Card className="border-slate-200 bg-slate-50/30">
              <CardHeader className="bg-slate-100">
                <CardTitle className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-slate-200 border-slate-300 text-slate-700">Non-Aktif</Badge>
                  Mitra Non-Aktif
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'INACTIVE')}>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">No</TableHead>
                          <TableHead>Nama Mitra</TableHead>
                          <TableHead>Jabatan</TableHead>
                          <TableHead>Nama KTP</TableHead>
                          <TableHead>No. KTP</TableHead>
                          <TableHead>Foto KTP</TableHead>
                          <TableHead>Kepemilikan Saham</TableHead>
                          <TableHead>Keterangan</TableHead>
                          {canManageManagers && <TableHead className="text-right">Aksi</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <SortableContext 
                        items={sortedManagers.filter(m => m.status === 'INACTIVE').map(m => m.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <TableBody>
                          {sortedManagers.filter(m => m.status === 'INACTIVE').map((manager, index) => (
                            <SortableManagerRow key={manager.id} manager={manager} index={index} />
                          ))}
                        </TableBody>
                      </SortableContext>
                    </Table>
                  </div>
                </DndContext>
              </CardContent>
            </Card>
          )}

          {/* Mitra Blacklist */}
          {sortedManagers.filter(m => m.status === 'BLACKLIST').length > 0 && (
            <Card className="border-red-200 bg-red-50/30">
              <CardHeader className="bg-red-50">
                <CardTitle className="flex items-center gap-2">
                  <Badge variant="destructive">Blacklist</Badge>
                  Mitra Blacklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'BLACKLIST')}>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">No</TableHead>
                          <TableHead>Nama Mitra</TableHead>
                          <TableHead>Jabatan</TableHead>
                          <TableHead>Nama KTP</TableHead>
                          <TableHead>No. KTP</TableHead>
                          <TableHead>Foto KTP</TableHead>
                          <TableHead>Kepemilikan Saham</TableHead>
                          <TableHead>Keterangan</TableHead>
                          {canManageManagers && <TableHead className="text-right">Aksi</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <SortableContext 
                        items={sortedManagers.filter(m => m.status === 'BLACKLIST').map(m => m.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <TableBody>
                          {sortedManagers.filter(m => m.status === 'BLACKLIST').map((manager, index) => (
                            <SortableManagerRow key={manager.id} manager={manager} index={index} />
                          ))}
                        </TableBody>
                      </SortableContext>
                    </Table>
                  </div>
                </DndContext>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {/* Image Preview Dialog */}
      <Dialog open={imagePreviewOpen} onOpenChange={setImagePreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{imagePreviewTitle}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <img 
              src={imagePreviewUrl} 
              alt={imagePreviewTitle}
              className="max-h-[70vh] object-contain rounded"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Managers;
